import type {
  ApiError,
  ApprovalResultDto,
  ApproveContentInput,
  AuthenticationResponseJSON,
  PasskeyOptionsResponse,
  RegistrationResponseJSON,
  SessionUser,
  ChangeRequestResultDto,
  CreatePrivacyRequest,
  CreateTrendTopicResultDto,
  CursorPage,
  EmergencyPauseInput,
  LeadConversationDetailDto,
  LeadConversationDto,
  PrivacyRequestDetailDto,
  PrivacyRequestDto,
  PrivacyResolutionRequest,
  PrivacyResolutionResponse,
  RedeemWhatsappCodeRequest,
  PanelRunStatusDto,
  RegenerateContentInput,
  RegenerateResultDto,
  RejectContentInput,
  RejectResultDto,
  OperationStateDto,
  PanelSnapshotDto,
  RequestContentChangeInput,
  VerifyPrivacyRequest,
  ViralRadarDto,
  WhatsappLinkDto,
} from '@devmil/contracts';

/**
 * Única porta pela qual o painel fala com o mundo.
 *
 * Nenhum componente chama a Meta, banco ou endpoint diretamente. Hoje quem
 * implementa isto é `FixturePanelDataSource`; os planos 02, 04, 05 e 06 trocam
 * a implementação por adaptadores HTTP sem tocar em JSX, CSS ou baseline.
 */
/**
 * Resgate manual do código do WhatsApp.
 *
 * O pseudônimo endereça a conversa e vai no caminho; código e telefone são o
 * segredo e vão no CORPO. Separar os dois no tipo é o que impede alguém de,
 * mais tarde, montar a URL com os três juntos: query string entra em log de
 * servidor, em histórico de navegador e no `Referer` da próxima requisição.
 */
export interface RedeemWhatsappCodeInput extends RedeemWhatsappCodeRequest {
  pseudonym: string;
}

/**
 * A sessão como a tela precisa dela. O token CSRF que viaja no mesmo envelope
 * NÃO passa por aqui: ele fica dentro do adaptador HTTP, em memória, porque
 * token que sobe para a árvore de componentes acaba em prop, em log de erro ou
 * em ferramenta de depuração.
 */
export interface AuthSessionDto {
  user: SessionUser;
  /** Limite absoluto da sessão, herdado do login. ISO UTC. */
  expiresAt: string;
  /** Vencimento por ociosidade. É ANTES dele que o painel renova. ISO UTC. */
  idleExpiresAt: string;
}

/**
 * Porta de autenticação por passkey.
 *
 * Ela é um campo da `PanelDataSource`, e não uma segunda porta solta, para o
 * painel continuar com um caminho de rede só. O modo fixture declara `null`
 * aqui, e é ESSE `null` que mantém a suíte visual sem nenhuma tela de login:
 * sem porta, nenhum componente tem como oferecer a cerimônia.
 */
export interface PanelAuthPort {
  /** Opções da cerimônia de login. O e-mail identifica o owner. */
  loginOptions(email: string): Promise<PasskeyOptionsResponse>;
  loginVerify(input: {
    ceremonyId: string;
    response: AuthenticationResponseJSON;
  }): Promise<AuthSessionDto>;
  /**
   * Opções do cadastro inicial. O convite viaja SÓ no corpo do POST: a API o
   * consome já nesta chamada, então falha depois daqui exige convite novo.
   */
  bootstrapOptions(enrollmentToken: string): Promise<PasskeyOptionsResponse>;
  bootstrapVerify(input: {
    ceremonyId: string;
    response: RegistrationResponseJSON;
  }): Promise<AuthSessionDto>;
  reauthOptions(): Promise<PasskeyOptionsResponse>;
  reauthVerify(input: {
    ceremonyId: string;
    response: AuthenticationResponseJSON;
  }): Promise<{reauthenticatedUntil: string}>;
  /** Gira a sessão antes do vencimento por ociosidade. */
  refreshSession(): Promise<AuthSessionDto>;
  /** Sonda barata de sessão viva, para desempatar renovação entre abas. */
  me(): Promise<SessionUser>;
  logout(): Promise<void>;
}

/**
 * O que a rota de mídia do painel sabe servir.
 *
 * `cover` e `media` identificam UMA linha por versão. O slide precisa da
 * POSIÇÃO, e por isso ele é um objeto e não um terceiro literal: `'slide'` solto
 * seria um endereço que o servidor não sabe resolver, e a tela descobriria isso
 * com um 404 no meio da aprovação.
 */
export type PanelMediaTarget = 'cover' | 'media' | {slide: number};

export interface PanelDataSource {
  /**
   * `null` quando esta fonte não tem autenticação, que é o caso da fixture.
   * A porta de login do painel inteiro liga e desliga por este campo.
   */
  readonly auth: PanelAuthPort | null;

  getSnapshot(signal?: AbortSignal): Promise<PanelSnapshotDto>;
  /**
   * Endereço da mídia renderizada de uma versão, ou `null`.
   *
   * É a porta, e não um `fetch` solto no componente, pelo mesmo motivo de todo
   * o resto deste arquivo: o painel fala com o mundo por um caminho só. Uma
   * `<img src>` montada dentro do JSX seria um segundo caminho de rede, invisível
   * para quem lê a porta e impossível de trocar por dublê no teste.
   *
   * Devolve URL (e não bytes) de propósito: quem sabe buscar mídia, retomar por
   * faixa e decodificar quadro é o navegador. O `null` da fonte simulada é o
   * que mantém a prévia esquemática no modo fixture, sem nenhum `if` de
   * ambiente dentro do componente.
   */
  mediaUrl(contentVersionId: string, role: PanelMediaTarget): string | null;
  approveContent(input: ApproveContentInput): Promise<ApprovalResultDto>;
  requestChange(
    input: RequestContentChangeInput,
  ): Promise<ChangeRequestResultDto>;
  rejectContent(input: RejectContentInput): Promise<RejectResultDto>;
  /**
   * Regenerar: aposenta a versão atual e manda a esteira produzir outra.
   *
   * É a única ação da porta que não termina no banco. As três decisões acima
   * gravam o que o dono decidiu; esta dispara trabalho, e por isso a resposta
   * diz `regenerando` e não `regenerado`: quando ela chega, a versão nova
   * existe VAZIA e a reexecução do dia ainda está correndo.
   */
  /**
   * O ESTADO da execução do dia, para a aba que acabou de regenerar.
   *
   * Ela existe porque `regenerateContent` devolve uma versão VAZIA e a peça
   * volta minutos depois, quando a reexecução termina. Até 2026-09-06 o painel
   * mostrava um toast e nunca mais falava do assunto (achado F12): a aba ficava
   * parada e uma falha de geração não aparecia em lugar nenhum.
   *
   * Leitura pura, e o painel a chama com intervalo e TETO. Ver
   * `acompanharRegeneracao`, no `PanelProvider`.
   */
  getRunStatus(runId: string, signal?: AbortSignal): Promise<PanelRunStatusDto>;
  regenerateContent(
    input: RegenerateContentInput,
  ): Promise<RegenerateResultDto>;
  setEmergencyPause(input: EmergencyPauseInput): Promise<OperationStateDto>;

  // --- Funil MAPA -------------------------------------------------------
  listLeadConversations(
    signal?: AbortSignal,
  ): Promise<CursorPage<LeadConversationDto>>;
  getLeadConversation(
    pseudonym: string,
    signal?: AbortSignal,
  ): Promise<LeadConversationDetailDto>;
  redeemWhatsappCode(input: RedeemWhatsappCodeInput): Promise<WhatsappLinkDto>;

  // --- Direitos do titular ---------------------------------------------
  listPrivacyRequests(
    signal?: AbortSignal,
  ): Promise<CursorPage<PrivacyRequestDto>>;
  createPrivacyRequest(input: CreatePrivacyRequest): Promise<PrivacyRequestDto>;
  getPrivacyRequest(
    requestId: string,
    signal?: AbortSignal,
  ): Promise<PrivacyRequestDetailDto>;
  verifyPrivacyRequest(
    requestId: string,
    input: VerifyPrivacyRequest,
  ): Promise<PrivacyRequestDto>;
  resolvePrivacyRequest(
    requestId: string,
    input: PrivacyResolutionRequest,
  ): Promise<PrivacyResolutionResponse>;

  // --- Radar de virais --------------------------------------------------
  /**
   * O radar do dia, com os TRÊS estados que o contrato prevê.
   *
   * Ele NÃO entra no snapshot do painel, e a separação é de propósito: o
   * snapshot é lido por toda tela a cada carga, e o radar depende de uma
   * credencial que hoje não existe. Pendurá-lo ali faria a falta de uma
   * credencial de leitura de terceiro atrasar (ou derrubar) a fila de
   * aprovação, que é a tela de que o dono realmente depende.
   */
  getViralRadar(signal?: AbortSignal): Promise<ViralRadarDto>;
  /**
   * Transforma a tendência em pauta NOSSA, e nada além disso.
   *
   * O desfecho é uma linha no banco, não uma publicação: a peça que sair dessa
   * pauta ainda passa por geração, pelos sete portões e pela aprovação humana
   * com passkey. Não existe, aqui nem em lugar nenhum desta porta, ação que
   * republique, baixe ou interaja com a mídia de terceiro.
   */
  createTrendTopic(viralPostId: string): Promise<CreateTrendTopicResultDto>;
}

/** O subconjunto de escrita, para componentes que só agem e não leem. */
export type PanelActions = Pick<
  PanelDataSource,
  | 'approveContent'
  | 'requestChange'
  | 'rejectContent'
  | 'getRunStatus'
  | 'regenerateContent'
  | 'setEmergencyPause'
>;

/** Erro de borda no formato que a API usará, para a UI ter um caminho só. */
export class PanelApiError extends Error {
  readonly payload: ApiError;

  /**
   * Status HTTP da resposta, quando houve resposta.
   *
   * A tela precisa distinguir 409 de 428 e de 429 para escolher a mensagem e,
   * no caso das precondições, para pedir a prova que falta em vez de mostrar
   * erro cru. O `code` do envelope sozinho não serve: ele é escrito pelo
   * servidor e muda de rota para rota, enquanto o status é contrato de HTTP.
   * `null` quando o erro nasceu dentro do painel, sem ida à rede.
   */
  readonly status: number | null;

  constructor(payload: ApiError, status: number | null = null) {
    super(payload.message);
    this.name = 'PanelApiError';
    this.payload = payload;
    this.status = status;
  }
}

export const isPanelApiError = (error: unknown): error is PanelApiError =>
  error instanceof PanelApiError;
